import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';
import { MAINTENANCE_EVENT } from '../services/maintenanceEvents';
import { logger } from '../services/logger';

interface MaintenanceContextType {
  isMaintenanceMode: boolean;
}

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

const HEALTH_POLL_INTERVAL_MS = 10_000;

/** Dedicated axios instance for health checks — bypasses the app interceptors. */
const healthClient = axios.create({ baseURL: API_BASE_URL, timeout: 5_000 });

export function MaintenanceProvider({ children }: { children: ReactNode }) {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const returnPath = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;

    pollRef.current = setInterval(async () => {
      try {
        await healthClient.get('/health');
        logger.info('Backend health check passed — exiting maintenance mode');
        setIsMaintenanceMode(false);

        // Redirect back to where the user was (or home for external visitors)
        const target = returnPath.current && returnPath.current !== '/maintenance'
          ? returnPath.current
          : '/';
        returnPath.current = null;
        window.location.href = target;
      } catch {
        // Still down — keep polling
      }
    }, HEALTH_POLL_INTERVAL_MS);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Listen for the backend-unavailable event from the API interceptor
  useEffect(() => {
    const handler = () => {
      if (!isMaintenanceMode) {
        const currentPath = window.location.pathname + window.location.search;
        returnPath.current = currentPath;
        logger.warn('Backend unavailable — entering maintenance mode', { returnPath: currentPath });
        setIsMaintenanceMode(true);
      }
    };

    window.addEventListener(MAINTENANCE_EVENT, handler);
    return () => window.removeEventListener(MAINTENANCE_EVENT, handler);
  }, [isMaintenanceMode]);

  // Start/stop health polling when maintenance mode changes
  useEffect(() => {
    if (isMaintenanceMode) {
      startPolling();
    } else {
      stopPolling();
    }
    return stopPolling;
  }, [isMaintenanceMode, startPolling, stopPolling]);

  return (
    <MaintenanceContext.Provider value={{ isMaintenanceMode }}>
      {children}
    </MaintenanceContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMaintenance(): MaintenanceContextType {
  const context = useContext(MaintenanceContext);
  if (context === undefined) {
    throw new Error('useMaintenance must be used within a MaintenanceProvider');
  }
  return context;
}
